var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams, useBlocker } from "react-router-dom";
import { usePaymentMethodFetch } from "../Account/BillingInformation/Shared/hooks/PaymentMethodFetch/usePaymentMethodFetch";
import { useAddresses } from "../Addresses/useAddresses";
import { useRecipeSelection } from "../OrderFlow/hooks/RecipeSelection/useRecipeSelection";
import { useHelpers } from "../OrderFlow/hooks/useHelpers";
import { useGetModel } from "../OrderFlow/OrderFlowProvider/OrderFlow.requests";
import { useOrderFlow } from "../OrderFlow/OrderFlowProvider/OrderFlowContext";
import { DEFAULT_DAY_COUNT, ModuleNames, RecipeIngredientReplacementMode, RecipeSelectionType, } from "../OrderFlow/OrderFlowProvider/OrderFlowTypes";
import { useCompany } from "../Shared/components/CompanyProvider/CompanyProvider";
import { CartSelectionModeType } from "../Shared/components/OrderSummary/OrderSummary.request";
import { LogLevel } from "../Shared/hooks/useLogging/Logging-types";
import { useLogging } from "../Shared/hooks/useLogging/useLogging";
import { CheckoutContext } from "./CheckoutContext";
import { getAddressForZip, getCheckoutInfo } from "./checkoutUtils";
import { PaymentSectionText } from "./PaymentSection/PaymentSection-types";
import { Routes } from "../Shared/components/Navigation/navigationUtils";
export const useCheckout = () => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const [data, setData] = useState();
    const [configText, setConfigText] = useState();
    const [loading, setLoading] = useState(true);
    const [isNavigationWarningOpen, setIsNavigationWarningOpen] = useState(false);
    const [pendingNavigation, setPendingNavigation] = useState(null);
    const [isLeavingPage, setIsLeavingPage] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const giftCardState = (_a = location.state) === null || _a === void 0 ? void 0 : _a.giftCardState;
    const { setCustomerShippingAddresses, setOrderInfo, orderInfo } = useContext(CheckoutContext);
    const { getPaymentMethods } = usePaymentMethodFetch();
    const { culture: { currency }, mobileRedirectSuccessUrl, mobileRedirectFailureUrl, } = useCompany();
    const { logReactPixel, logReactPixelPageView, createKlaviyoEvent, gaEvent, gaPageView, datadogLog } = useLogging();
    const orderFlowState = (_b = location.state) === null || _b === void 0 ? void 0 : _b.orderFlowState;
    const { loading: isModelLoading, setLoading: setModelLoading, getModel } = useGetModel();
    const { store } = useOrderFlow();
    const pathParams = useParams();
    const { getDayFilterDays } = useHelpers();
    const { getRecipes, getMealsWithSelectionSource } = useRecipeSelection();
    const { addressState: { loadingAddresses, deliveryQuestions }, } = useAddresses();
    const sku = (_e = (_c = pathParams === null || pathParams === void 0 ? void 0 : pathParams.sku) !== null && _c !== void 0 ? _c : (_d = store === null || store === void 0 ? void 0 : store.model) === null || _d === void 0 ? void 0 : _d.Sku) !== null && _e !== void 0 ? _e : "";
    useEffect(() => {
        let mounted = true;
        const loadData = () => __awaiter(void 0, void 0, void 0, function* () {
            var _a;
            try {
                // If the store is empty and it's not a gift card order, load from a SavedCart
                if (!store.cart.startDate && !giftCardState) {
                    yield getModel();
                }
                getPaymentMethods();
                const checkoutInfo = yield getCheckoutInfo(sku, datadogLog, orderFlowState === null || orderFlowState === void 0 ? void 0 : orderFlowState.orderInfo);
                setData(checkoutInfo);
                setCustomerShippingAddresses(checkoutInfo === null || checkoutInfo === void 0 ? void 0 : checkoutInfo.customerShippingAddresses);
                setConfigText(checkoutInfo === null || checkoutInfo === void 0 ? void 0 : checkoutInfo.checkoutConfigurableText);
                setOrderInfo(checkoutInfo === null || checkoutInfo === void 0 ? void 0 : checkoutInfo.orderInfo);
            }
            catch (error) {
                console.error(error);
                datadogLog((_a = error.message) !== null && _a !== void 0 ? _a : "Caught exception in useCheckout", LogLevel.ERROR, error);
            }
            finally {
                setLoading(false);
                setModelLoading(undefined);
            }
        });
        if (mounted) {
            loadData();
        }
        return () => {
            mounted = false;
        };
    }, []);
    useEffect(() => {
        var _a;
        const meals = (_a = orderInfo === null || orderInfo === void 0 ? void 0 : orderInfo.days) === null || _a === void 0 ? void 0 : _a.flatMap((day) => day.meal_dates.flatMap((mealDate) => mealDate.meal_list.flatMap((meal) => {
            return {
                id: meal.ProgramMealId,
                quantity: meal.quantity,
                item_price: meal.meal_cost,
            };
        })));
        logReactPixel("InitiateCheckout", {
            value: orderInfo === null || orderInfo === void 0 ? void 0 : orderInfo.total,
            currency: currency,
            contents: meals,
            content_type: "product",
        });
        logReactPixelPageView();
        createKlaviyoEvent("InitiateCheckout", {
            CurrencyType: currency,
            Total: orderInfo === null || orderInfo === void 0 ? void 0 : orderInfo.total,
        });
        createKlaviyoEvent("PageView", {
            SelectedLocation: "Checkout",
        });
        gaEvent("begin_checkout", {
            currency: currency,
            total: orderInfo === null || orderInfo === void 0 ? void 0 : orderInfo.total,
        });
        gaPageView("Checkout", location.pathname);
    }, [orderInfo]);
    useEffect(() => {
        var _a, _b, _c;
        let mounted = true;
        if (!mounted)
            return;
        // After loading from a SavedCart we have to fetch recipes if the cart can be edited
        if (!isModelLoading &&
            store.cart.meals.length > 0 &&
            store.model.Recipes.length === 0 &&
            ((_a = store.model.CartSettings) === null || _a === void 0 ? void 0 : _a.CartSelectionMode) !== CartSelectionModeType.NONE) {
            const dayFilterDays = getDayFilterDays(store.cart.daysOfWeek, store.model.Recipes, store.model.DeliveryDays, store.cart.startDate, store.cart.isSubscription, store.cart.subscriptionPeriod, store.cart.oneTimeOrderPeriod, false, 
            // Include all possible days, regardless of whether a menu is set, to ensure no days are excluded.
            false, store.model.GeneralSettings.OnlyShowDeliveryDays, DEFAULT_DAY_COUNT, store.cart.meals, false);
            const flowContainsMealQuantityModule = store.model.Flow.FlowModules.find((module) => module.ModuleName === ModuleNames.MEAL_QUANTITY);
            const flowContainsMealBundleChooser = store.model.Flow.FlowModules.find((module) => module.ModuleName === ModuleNames.MEAL_BUNDLE_CHOOSER);
            const recipeFetchFlags = {
                shouldAutoSelectRecipes: false,
                shouldReplaceIngredients: store.model.OrderFlowSettings.RecipeIngredientReplacementMode === RecipeIngredientReplacementMode.ENABLED,
            };
            // TODO: This can be improved with graph to load only the recipes we need
            if (store.model.Recipes.length === 0) {
                getRecipes(recipeFetchFlags, store.cart.meals, {
                    ProgramSKU: store.model.Program.Sku,
                    RecipeTypeId: (_c = (_b = store.cart.recipeType) === null || _b === void 0 ? void 0 : _b.recipeTypeId) !== null && _c !== void 0 ? _c : 0,
                    Meals: getMealsWithSelectionSource(store.cart.meals, store.model.Program.ProgramMeals, store.cart.mealSize, store.model.allMealSizeOption, RecipeSelectionType.AssignRecipes, !flowContainsMealQuantityModule || !flowContainsMealBundleChooser, store.cart.meals.map((m) => m.ProgramMealId), dayFilterDays, store.model.ExplodeOMDD, false, store.model.GeneralSettings.OnlyShowDeliveryDays),
                    Preferences: store.cart.omissions.map((omission) => {
                        return {
                            CategoryName: omission.categoryName,
                            IngredientMajorID: omission.dislikeId,
                            IngredientMajorName: omission.dislikeName,
                        };
                    }),
                    AssignConflictingRecipes: recipeFetchFlags.shouldReplaceIngredients,
                });
            }
        }
        return () => {
            mounted = false;
        };
    }, [isModelLoading, store.cart.meals]);
    let addressToUse = undefined;
    const matchingAddresses = getAddressForZip(store.model.PostalCode, data === null || data === void 0 ? void 0 : data.customerShippingAddresses);
    if ((_f = location === null || location === void 0 ? void 0 : location.state) === null || _f === void 0 ? void 0 : _f.addressId) {
        addressToUse = matchingAddresses === null || matchingAddresses === void 0 ? void 0 : matchingAddresses.find((a) => { var _a; return a.addressId === Number((_a = location === null || location === void 0 ? void 0 : location.state) === null || _a === void 0 ? void 0 : _a.addressId); });
    }
    else if ((matchingAddresses === null || matchingAddresses === void 0 ? void 0 : matchingAddresses.length) == 1) {
        addressToUse = matchingAddresses[0];
    }
    const blocker = useBlocker(({ currentLocation, nextLocation }) => !isLeavingPage &&
        currentLocation.pathname.includes(Routes.CHECKOUT) &&
        !nextLocation.pathname.includes(Routes.CHECKOUT) &&
        !nextLocation.pathname.includes(Routes.ORDER_FLOW) &&
        !nextLocation.pathname.includes(Routes.THANK_YOU) &&
        (mobileRedirectSuccessUrl !== "" && mobileRedirectSuccessUrl !== undefined ? !nextLocation.pathname.includes(new URL(mobileRedirectSuccessUrl).pathname) : true) &&
        (mobileRedirectFailureUrl !== "" && mobileRedirectFailureUrl !== undefined ? !nextLocation.pathname.includes(new URL(mobileRedirectFailureUrl).pathname) : true));
    const handleNavigationAttempt = (path, state) => {
        setPendingNavigation({ path, state });
        setIsNavigationWarningOpen(true);
    };
    const handleStayOnPage = () => {
        setIsNavigationWarningOpen(false);
    };
    const handleLeavePage = () => {
        var _a;
        setIsNavigationWarningOpen(false);
        setIsLeavingPage(true);
        if (blocker.state === "blocked") {
            (_a = blocker.proceed) === null || _a === void 0 ? void 0 : _a.call(blocker);
        }
        if (pendingNavigation) {
            navigate(pendingNavigation.path, { state: pendingNavigation.state });
        }
        setPendingNavigation(null);
    };
    useEffect(() => {
        var _a, _b;
        if (blocker.state === "blocked" && !isLeavingPage) {
            const targetPath = ((_a = blocker.location) === null || _a === void 0 ? void 0 : _a.pathname) || '/';
            let targetState = (_b = blocker.location) === null || _b === void 0 ? void 0 : _b.state;
            handleNavigationAttempt(targetPath, targetState);
        }
    }, [blocker, isLeavingPage]);
    return {
        paymentSectionTitle: configText === null || configText === void 0 ? void 0 : configText.paymentSectionTitle,
        paymentSectionText: configText === null || configText === void 0 ? void 0 : configText.paymentSectionText,
        shouldShowTermsCheckbox: !!(configText === null || configText === void 0 ? void 0 : configText.termsAndConditionsText),
        termsAndConditionsTitle: configText === null || configText === void 0 ? void 0 : configText.termsAndConditionsTitle,
        termsAndConditionsText: configText === null || configText === void 0 ? void 0 : configText.termsAndConditionsText,
        deliverySectionTitle: configText === null || configText === void 0 ? void 0 : configText.deliverySectionTitle,
        deliverySectionText: configText === null || configText === void 0 ? void 0 : configText.deliverySectionText,
        questionSectionTitle: configText === null || configText === void 0 ? void 0 : configText.questionSectionTitle,
        questionSectionText: configText === null || configText === void 0 ? void 0 : configText.questionSectionText,
        pickUpLocations: data === null || data === void 0 ? void 0 : data.pickUpLocations,
        primaryShippingAddress: data === null || data === void 0 ? void 0 : data.primaryShippingAddress,
        selectedAddress: addressToUse,
        pickupQuestions: data === null || data === void 0 ? void 0 : data.pickupQuestions,
        deliveryQuestions: deliveryQuestions,
        coolerChargeText: data === null || data === void 0 ? void 0 : data.coolerChargeText,
        skipPayment: data === null || data === void 0 ? void 0 : data.skipPayment,
        validEmail: data === null || data === void 0 ? void 0 : data.validEmail,
        emailErrorText: data === null || data === void 0 ? void 0 : data.emailErrorText,
        enableGiftCards: data === null || data === void 0 ? void 0 : data.enableGiftCards,
        loading: loading || Boolean(isModelLoading) || loadingAddresses,
        overlappingStatus: data === null || data === void 0 ? void 0 : data.overlappingStatus,
        canPayLater: (_g = data === null || data === void 0 ? void 0 : data.canPayLater) !== null && _g !== void 0 ? _g : false,
        payLaterText: (_h = data === null || data === void 0 ? void 0 : data.payLaterText) !== null && _h !== void 0 ? _h : PaymentSectionText.PAY_LATER,
        specialInstructions: data === null || data === void 0 ? void 0 : data.specialInstructions,
        isNavigationWarningOpen,
        handleStayOnPage,
        handleLeavePage
    };
};
